import type { AuthPlugin } from '~/plugins/auth/index'
import type { GrantsPlugin } from '~/plugins/auth/grants'
import type { FeaturesPlugin } from '~/plugins/auth/features'
import type { GatesPlugin } from '~/plugins/auth/gates'

/**
 * Composition API wrapper around the $auth, $grants, $features, and $gates plugins.
 *
 * Usage:
 *   const { auth, grants, features, gates } = useAuth()
 *   const info = auth.info
 *   const canEdit = grants.get({ model: 'customer', actions: ['edit'] })
 *
 * This composable is built in Phase 1 and adopted as pages convert in Phases 3-4.
 * In Phase 6.4, the underlying plugin will be replaced with direct Pinia store access.
 */
export function useAuth() {
  const nuxtApp = useNuxtApp()

  const auth = nuxtApp.$auth as AuthPlugin
  const grants = (nuxtApp as any).$grants as GrantsPlugin
  const features = (nuxtApp as any).$features as FeaturesPlugin
  const gates = (nuxtApp as any).$gates as GatesPlugin

  // Reactive computed wrappers for commonly used getters
  const isLoggedIn = computed(() => !!auth.active)
  const isLocked = computed(() => auth.locked)
  const isReady = computed(() => auth.ready)
  const activeAccount = computed(() => auth.active)
  const info = computed(() => auth.info)
  const theme = computed(() => auth.theme)

  return {
    // Plugin instances (full API access)
    auth,
    grants,
    features,
    gates,

    // Reactive computed shortcuts
    isLoggedIn,
    isLocked,
    isReady,
    activeAccount,
    info,
    theme,
  }
}
